import { PageHeader , Button , Card, CardContent, CardHeader, CardTitle , Skeleton } from "@roberttamas/ui";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Download } from "@/components/icons";
import { TranslationKeysForm } from "@/components/translation-keys-form";
import { getTranslations } from "@/lib/api";
import { formatLocaleLabelWithTag } from "@/lib/locales";
import {
  buildTranslationEditPath,
  exportTranslationsJson,
} from "@/lib/translations";
import type { GetTranslationsResp } from "@/lib/types";

export function TranslationDetailPage() {
  const { t } = useTranslation();
  const { variant = "" } = useParams<{ variant: string }>();

  const query = useQuery<GetTranslationsResp>({
    queryKey: ["translations", variant],
    queryFn: () => getTranslations({ variant }),
    enabled: variant.length > 0,
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">{t("translations.loadOneFailed")}</p>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("translations.title")}
        backTo="/translations"
        backLabel={t("translations.backToTranslations")}
        title={formatLocaleLabelWithTag(query.data.variant)}
        subtitle={t("translations.detailSubtitle")}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => exportTranslationsJson(query.data.variant, query.data.keys)}
            >
              <Download className="h-4 w-4" />
              {t("translations.download")}
            </Button>
            <Button asChild>
              <Link to={buildTranslationEditPath(query.data.variant)}>{t("common.edit")}</Link>
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">{t("translations.valuesTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <TranslationKeysForm keys={query.data.keys} readOnly />
        </CardContent>
      </Card>
    </div>
  );
}